/**
 * ============================================================================
 * SessionReplay System v2 — Tracking + Session-Recording (rrweb) für Shopify
 * ============================================================================
 * Datei:      assets/session-replay.js
 * Einbindung: über snippets/session-replay.liquid ({% render 'session-replay' %})
 *
 * v2-Neuerungen:
 *  - Vollständiges Session-Recording via rrweb (Video-artiges Replay)
 *  - rrweb wird vom eigenen Backend geladen (kein CDN), NUR bei Consent
 *  - Chunk-Upload über den Shopify App Proxy an die Custom App
 *  - JS-Fehler des Besuchers werden als Custom-Events mit aufgezeichnet
 *  - Analytics-Events (click, cart, page_view) doppeln als Timeline-Marker
 *  - mousemove/scroll/hover aus dem Event-Tracking entfernt (steckt im Recording)
 *
 * DSGVO: sensible Eingaben maskiert, normale Suchfelder lesbar, Passwortfelder ignoriert,
 * Aufzeichnung nur bei Analytics-Consent (Shopify Customer Privacy API).
 * ============================================================================
 */
(function () {
  'use strict';

  var RECORDER_VERSION = '3.1.6';

  // Wiedereintritts-Schutz: läuft das Skript aus irgendeinem Grund zweimal auf
  // derselben Seite (z. B. Shopify Section-Rendering-API/AJAX-Reload, doppelt
  // eingebundenes App-Block-Snippet, bfcache-Resume), würden zwei unabhängige
  // rrweb-Recorder mit demselben sessionId gleichzeitig aufnehmen. Beide läsen
  // den gleichen Chunk-Zähler und würden ihn unkoordiniert
  // hochzählen -> doppelt vergebene Chunk-Nummern und zwei sich überlagernde
  // Aufnahmen (zwei FullSnapshots/Node-ID-Räume) für dieselbe Session. Das
  // reißt das Replay am Übergang der zweiten Aufnahme ab (weißer Bildschirm
  // im Player). Zweite Ausführung daher komplett überspringen.
  if (window.__sessionReplayLoaded) return;
  window.__sessionReplayLoaded = true;

  var CONFIG = {
    endpoint: '/apps/session-replay/events',
    recording: {
      enabled: true,
      endpoint: '/apps/session-replay/recordings',
      // rrweb-record, self-hosted auf dem eigenen Backend (kein Dritt-CDN)
      // rrweb 2.x; die Version steht im Dateinamen (kein Cache-Konflikt mit 1.x)
      libUrl: 'https://tracking.75-119-146-154.sslip.io/assets/rrweb-record-2.1.6.min.js',
      // Direkter Geo-Ping (NICHT über App Proxy) — nur so sieht der Server die echte Besucher-IP
      geoUrl: 'https://tracking.75-119-146-154.sslip.io/geo',
      chunkIntervalMs: 10000,   // Chunk spätestens alle 10 s hochladen
      maxBufferEvents: 400,     // ... oder ab dieser Eventanzahl
      maxRetryBuffer: 4000,     // Obergrenze ungesendeter rrweb-Events
      maxChunkBytes: 900000,    // Zielgröße pro Upload in echten Bytes (nicht Zeichen)
      maxChunkEvents: 900,      // harte Backend-Grenze ist 1000 Events pro Chunk
      gzipUploads: true,        // Body gzip-komprimieren, wenn der Browser es kann
      maxRetryBatches: 80,
      // Beim Verlassen der Seite: Browser erlauben fuer alle keepalive-Requests
      // und Beacons zusammen nur 64 KiB in Flug. Der Rest wird synchron in
      // localStorage geparkt und beim naechsten Seitenaufruf nachgeliefert.
      unloadBeaconBytes: 56000,
      unloadChunkBytes: 50000,
      retryIntervalMs: 30000,   // Warteschlange regelmäßig abarbeiten, nicht nur beim Seitenaufruf
      retryMaxAgeMs: 24 * 60 * 60 * 1000,
      recordCanvas: true,       // Canvas-Inhalte aufzeichnen (z. B. Gravur-Vorschau)
      canvasFps: 4              // Canvas-Sampling (Bilder/s) — höher = mehr Daten
    },
    maxRecentProducts: 20,
    productTtlDays: 30,
    batchIntervalMs: 10000,
    batchSize: 5,
    maxQueuedEvents: 500,
    hoverMinMs: 400,
    requireConsent: true,
    storagePrefix: 'sr:',
    // Nach so viel echter Inaktivität (keine Klicks/Tasten/Scroll) gilt eine
    // wiederkehrende Nutzung als neue Session statt als Fortsetzung der alten
    // (verhindert z. B. "Sessions" über mehrere Stunden durch offen gelassene Tabs).
    idleTimeoutMs: 30 * 60 * 1000
  };

  function now() { return Date.now(); }
  // Das Backend begrenzt die Nutzlast nach Bytes, JSON.stringify(...).length
  // zählt aber UTF-16-Einheiten. Bei Umlauten/Emoji ist der Upload real größer
  // als gezählt — deshalb immer echte UTF-8-Bytes messen, sonst laufen Chunks
  // trotz eingehaltenem Limit in einen 413.
  var byteCounter = window.TextEncoder ? new TextEncoder() : null;
  function jsonBytes(value) {
    var json = typeof value === 'string' ? value : JSON.stringify(value);
    if (json == null) return 0;
    try {
      if (byteCounter) return byteCounter.encode(json).length;
      if (window.Blob) return new Blob([json]).size;
    } catch (e) { }
    return json.length;
  }
  function safeParse(json, fallback) { try { return JSON.parse(json); } catch (e) { return fallback; } }

  var storage = {
    get: function (key, fallback) {
      try {
        var raw = window.localStorage.getItem(CONFIG.storagePrefix + key);
        return raw === null ? fallback : safeParse(raw, fallback);
      } catch (e) { return fallback; }
    },
    set: function (key, value) {
      try { window.localStorage.setItem(CONFIG.storagePrefix + key, JSON.stringify(value)); } catch (e) { }
    },
    remove: function (key) {
      try { window.localStorage.removeItem(CONFIG.storagePrefix + key); } catch (e) { }
    }
  };

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  // Kundenkennung: Customer ID (eingeloggt) oder persistente Gast-ID
  function getCustomerKey() {
    var cid = window.__SR_CUSTOMER_ID || null;
    if (cid) return 'customer:' + cid;
    var guestId = storage.get('guestId', null);
    if (!guestId) { guestId = uuid(); storage.set('guestId', guestId); }
    return 'guest:' + guestId;
  }

  // Session = Besuch: Die Session-ID liegt im localStorage und gilt fuer alle
  // Tabs dieses Browsers, bis CONFIG.idleTimeoutMs lang nirgends Aktivitaet war.
  // Neue, kopierte oder aus dem Checkout zurueckkehrende Tabs muessen deshalb
  // nicht erkannt werden. Jeder Seitenaufruf nimmt als eigener Abschnitt
  // (viewId) auf; der Player schneidet daraus anhand der Sichtbarkeit zusammen,
  // was der Besucher tatsaechlich gesehen hat.
  var sessionStore = {
    getItem: function (key) { try { return window.localStorage.getItem(key); } catch (e) { return null; } },
    setItem: function (key, value) { try { window.localStorage.setItem(key, value); } catch (e) { } },
    removeItem: function (key) { try { window.localStorage.removeItem(key); } catch (e) { } }
  };
  var viewId = uuid();

  // Recorder < 3.1 hielt die Session im sessionStore. Eine dort noch aktive
  // Session wird beim Update uebernommen, statt den Besuch zu teilen.
  function adoptLegacySession() {
    try {
      var legacy = window.sessionStorage;
      var sid = legacy.getItem(CONFIG.storagePrefix + 'sessionId');
      var last = Number(legacy.getItem(CONFIG.storagePrefix + 'lastActivityAt')) || 0;
      if (!sid || !last || now() - last > CONFIG.idleTimeoutMs) return null;
      SESSION_KEYS.concat(LEGACY_SESSION_KEYS).forEach(function (k) {
        var value = legacy.getItem(CONFIG.storagePrefix + k);
        if (value !== null && SESSION_KEYS.indexOf(k) !== -1) sessionStore.setItem(CONFIG.storagePrefix + k, value);
        legacy.removeItem(CONFIG.storagePrefix + k);
      });
      return sid;
    } catch (e) { return null; }
  }

  function getSessionId() {
    var sid = sessionStore.getItem(CONFIG.storagePrefix + 'sessionId');
    if (!sid) {
      sid = adoptLegacySession() || uuid();
      sessionStore.setItem(CONFIG.storagePrefix + 'sessionId', sid);
    }
    return sid;
  }

  // Consent (Shopify Customer Privacy API)
  // Wichtig: Die API ist NICHT automatisch global — sie muss über
  // Shopify.loadFeatures(['consent-tracking-api']) geladen werden.
  function hasConsent() {
    if (!CONFIG.requireConsent) return true;
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (privacy && typeof privacy.analyticsProcessingAllowed === 'function') {
        return privacy.analyticsProcessingAllowed();
      }
    } catch (e) { }
    return false;
  }

  // Lädt die Consent-Tracking-API nach und ruft cb auf, sobald sie bereit ist
  // (oder sofort, wenn Consent gar nicht erforderlich ist).
  function ensurePrivacyApi(cb) {
    if (!CONFIG.requireConsent) { cb(); return; }
    try {
      if (window.Shopify && window.Shopify.customerPrivacy && typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
        cb(); return;
      }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function () { cb(); });
        return;
      }
    } catch (e) { }
    cb(); // API nicht verfügbar — hasConsent() bleibt konservativ false
  }

  // --------------------------------------------------------------------------
  // Inaktivitäts-Erkennung: eine Session, die lange genug ohne echte
  // Nutzerinteraktion (in irgendeinem Tab) herumlag, wird verworfen — der
  // nächste Seitenaufruf startet dann eine frische Session, statt die alte
  // über Stunden/über Nacht künstlich zu verlängern.
  // --------------------------------------------------------------------------
  var SESSION_KEYS = [
    'sessionId', 'startedAt', 'pageCount', 'hasCart', 'errorCount', 'rejectionCount',
    'networkErrorCount', 'slowLoad', 'cartToken', 'lastActivityAt', 'referrer', 'gzip'
  ];
  var LEGACY_SESSION_KEYS = ['rrSeq', 'tabMarked', 'openerSessionId'];
  (function resetExpiredSession() {
    var last = Number(sessionStore.getItem(CONFIG.storagePrefix + 'lastActivityAt')) || 0;
    if (last && (now() - last) > CONFIG.idleTimeoutMs) {
      SESSION_KEYS.forEach(function (k) { sessionStore.removeItem(CONFIG.storagePrefix + k); });
    }
  })();

  var lastActivityWriteAt = 0;
  function markActivity() {
    if (!hasConsent()) return;
    var t = now();
    if (t - lastActivityWriteAt < 5000) return; // localStorage-Writes drosseln
    lastActivityWriteAt = t;
    sessionStore.setItem(CONFIG.storagePrefix + 'lastActivityAt', String(t));
  }
  var activityListenersStarted = false;
  function startActivityListeners() {
    if (activityListenersStarted) return;
    activityListenersStarted = true;
    ['click', 'keydown', 'scroll', 'touchstart', 'mousemove'].forEach(function (evt) {
      document.addEventListener(evt, markActivity, { passive: true, capture: true });
    });
    markActivity();
  }

  // Personenbeziehbare Kennungen entstehen erst nach bestätigtem Consent.
  var customerKey = null;
  var sessionId = null;
  var productsKey = null;
  var queueKey = null;
  var pending = [];
  function prepareIdentity() {
    if (customerKey && sessionId) return;
    customerKey = getCustomerKey();
    sessionId = getSessionId();
    productsKey = 'products:' + customerKey;
    queueKey = 'queue:' + customerKey;
    pending = storage.get(queueKey, []);
  }

  function stripUrlSecrets(value) {
    if (!value) return '';
    var raw = String(value);
    if (/^(?:data:image\/|blob:)/i.test(raw)) return raw;
    if (/^(?:mailto:|tel:)/i.test(raw)) return '';
    try { return new URL(raw, window.location.origin).pathname || '/'; }
    catch (e) { return raw.split(/[?#]/)[0]; }
  }

  // Beim Referrer ist der FREMDE Host die eigentliche Information ("kam ueber
  // google.com"). stripUrlSecrets wuerde davon nur den Pfad uebrig lassen und
  // damit genau die Aussage vernichten. Host und Pfad bleiben deshalb erhalten,
  // Query und Fragment (Suchbegriffe, Klick-IDs) nicht.
  function sanitizeReferrerUrl(value) {
    if (!value) return '';
    try {
      var url = new URL(String(value));
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';
      return url.origin + (url.pathname === '/' ? '' : url.pathname);
    } catch (e) { return ''; }
  }

  // Nur ein fremder Host ist ein Referrer. Ab dem zweiten Seitenaufruf zeigt
  // document.referrer auf den Shop selbst — ohne diese Pruefung wuerde aus
  // einem Direkteinstieg nachtraeglich ein interner Referrer.
  function externalReferrer() {
    var ref = document.referrer || '';
    if (!ref) return '';
    try { if (new URL(ref).origin === window.location.origin) return ''; } catch (e) { return ''; }
    return sanitizeReferrerUrl(ref);
  }

  // Es zaehlt der Referrer des ERSTEN Seitenaufrufs. Ein bereits gespeicherter
  // leerer Wert bedeutet "Direkteinstieg" und darf nicht ueberschrieben werden.
  function sessionReferrer() {
    var stored = null;
    try { stored = sessionStore.getItem(CONFIG.storagePrefix + 'referrer'); } catch (e) { }
    return stored === null ? externalReferrer() : stored;
  }

  // Asset-URLs im DOM (Bilder, Stylesheets, Links) muessen im Replay weiterhin
  // ladbar bleiben: Origin und Pfad bleiben erhalten, aus dem Query-String
  // ueberleben nur die fuer die Darstellung noetigen Shopify-CDN-Parameter.
  var SAFE_ASSET_PARAMS = /^(?:v|version|width|height|w|h|dpr|crop|format|quality|size|pad_color)$/i;
  function sanitizeAssetUrl(value) {
    if (!value) return '';
    var raw = String(value);
    if (/^(?:data:|blob:)/i.test(raw)) return raw;
    if (/^(?:mailto:|tel:)/i.test(raw)) return '';
    if (/^(?:#|javascript:)/i.test(raw)) return raw;
    try {
      var url = new URL(raw, window.location.origin);
      url.username = '';
      url.password = '';
      url.hash = '';
      var keep = [];
      url.searchParams.forEach(function (paramValue, name) {
        if (SAFE_ASSET_PARAMS.test(name)) keep.push([name, paramValue]);
      });
      url.search = '';
      keep.forEach(function (pair) { url.searchParams.append(pair[0], pair[1]); });
      // Absolut halten: im Player laeuft die Aufnahme auf dem Dashboard-Host,
      // relative Pfade wuerden dort ins Leere zeigen (keine Bilder/Styles).
      return url.href;
    } catch (e) { return raw.split(/[?#]/)[0]; }
  }

  function sanitizeAssetSrcset(value) {
    return String(value || '').split(',').map(function (part) {
      var match = /^(\s*)(\S+)(.*)$/.exec(part);
      return match ? match[1] + sanitizeAssetUrl(match[2]) + match[3] : part;
    }).join(',');
  }

  // --------------------------------------------------------------------------
  // Zuletzt angesehene Produkte (unverändert aus v1)
  // --------------------------------------------------------------------------
  function loadProducts() {
    if (!productsKey) return [];
    var list = storage.get(productsKey, []);
    var cutoff = now() - CONFIG.productTtlDays * 24 * 60 * 60 * 1000;
    var fresh = list.filter(function (p) { return p && p.lastViewedAt >= cutoff; });
    if (fresh.length !== list.length) storage.set(productsKey, fresh);
    return fresh;
  }

  function recordProductView(product) {
    if (!product || !product.id) return;
    var list = loadProducts();
    var existing = null;
    for (var i = 0; i < list.length; i++) {
      if (String(list[i].id) === String(product.id)) { existing = list.splice(i, 1)[0]; break; }
    }
    if (existing) {
      existing.lastViewedAt = now();
      existing.viewCount = (existing.viewCount || 1) + 1;
      existing.title = product.title || existing.title;
      existing.image = product.image || existing.image;
      existing.price = product.price != null ? product.price : existing.price;
      existing.url = stripUrlSecrets(product.url) || existing.url;
      list.unshift(existing);
    } else {
      list.unshift({
        id: product.id, title: product.title || '', image: product.image || '',
        price: product.price != null ? product.price : null, url: stripUrlSecrets(product.url),
        firstViewedAt: now(), lastViewedAt: now(), viewCount: 1
      });
    }
    if (list.length > CONFIG.maxRecentProducts) list.length = CONFIG.maxRecentProducts;
    storage.set(productsKey, list);
    renderWidget();
  }

  // --------------------------------------------------------------------------
  // Session-Metadaten (für Session-Liste im Dashboard)
  // --------------------------------------------------------------------------
  var meta = null;
  function prepareMeta() {
    if (meta) return;
    meta = {
      startedAt: Number(sessionStore.getItem(CONFIG.storagePrefix + 'startedAt')) || now(),
      pageCount: (Number(sessionStore.getItem(CONFIG.storagePrefix + 'pageCount')) || 0) + 1,
      hasCart: sessionStore.getItem(CONFIG.storagePrefix + 'hasCart') === '1',
      errorCount: Number(sessionStore.getItem(CONFIG.storagePrefix + 'errorCount')) || 0,
      rejectionCount: Number(sessionStore.getItem(CONFIG.storagePrefix + 'rejectionCount')) || 0,
      networkErrorCount: Number(sessionStore.getItem(CONFIG.storagePrefix + 'networkErrorCount')) || 0,
      slowLoad: sessionStore.getItem(CONFIG.storagePrefix + 'slowLoad') === '1',
      cartToken: sessionStore.getItem(CONFIG.storagePrefix + 'cartToken') || null,
      referrer: sessionReferrer()
    };
    try {
      sessionStore.setItem(CONFIG.storagePrefix + 'referrer', meta.referrer);
      sessionStore.setItem(CONFIG.storagePrefix + 'startedAt', String(meta.startedAt));
      sessionStore.setItem(CONFIG.storagePrefix + 'pageCount', String(meta.pageCount));
    } catch (e) { }
  }

  // Cart-Token holen (verknüpft die Session später mit der echten Bestellung).
  // /cart.js liefert den aktuellen Warenkorb-Token — derselbe landet in order.cart_token.
  function refreshCartToken() {
    if (!hasConsent() || !meta) return;
    try {
      fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); }).then(function (c) {
        if (c && c.token) { meta.cartToken = c.token; try { sessionStore.setItem(CONFIG.storagePrefix + 'cartToken', c.token); } catch (e) {} }
      }).catch(function () {});
    } catch (e) {}
  }

  function metaPayload() {
    return {
      startedAt: meta.startedAt,
      pageCount: meta.pageCount,
      hasCart: meta.hasCart,
      errorCount: meta.errorCount,
      rejectionCount: meta.rejectionCount,
      networkErrorCount: meta.networkErrorCount,
      slowLoad: meta.slowLoad,
      cartToken: meta.cartToken,
      referrer: meta.referrer,
      recorderVersion: RECORDER_VERSION,
      vw: window.innerWidth, vh: window.innerHeight,
      ua: navigator.userAgent,
      path: window.location.pathname
    };
  }

  function markCart() {
    if (!hasConsent() || !meta) return;
    meta.hasCart = true;
    try { sessionStore.setItem(CONFIG.storagePrefix + 'hasCart', '1'); } catch (e) { }
    refreshCartToken(); // Warenkorb-Token aktualisieren (für Kauf-Verknüpfung)
  }

  // --------------------------------------------------------------------------
  // Event-Queue (Analytics) mit Batching + Retry
  // --------------------------------------------------------------------------
  var flushTimer = null;

  function persistQueue() {
    if (!queueKey) return;
    if (pending.length > CONFIG.maxQueuedEvents) pending = pending.slice(pending.length - CONFIG.maxQueuedEvents);
    storage.set(queueKey, pending);
  }

  function track(type, data) {
    if (!hasConsent() || trackingExcluded) return;
    pending.push({
      eventId: uuid(), type: type, ts: now(), customerKey: customerKey, sessionId: sessionId,
      page: { path: window.location.pathname, template: window.__SR_PAGE_TYPE || null },
      product: window.__SR_PRODUCT ? { id: window.__SR_PRODUCT.id } : null,
      data: sanitizeRecordingValue(data || {}, '', [])
    });
    persistQueue();
    if (pending.length >= CONFIG.batchSize) flush();
    // Analytics-Events zusätzlich als Timeline-Marker ins Recording
    addRecordingMarker(type, data);
  }

  function flush(useBeacon) {
    if (!pending.length || !CONFIG.endpoint) return;
    var batch = pending.splice(0, pending.length);
    persistQueue();
    var batchId = 'events:' + (batch[0].eventId || uuid());
    var payload = JSON.stringify({ batchId: batchId, customerKey: customerKey, sessionId: sessionId, sentAt: now(), events: batch });
    if (useBeacon && navigator.sendBeacon) {
      var ok = navigator.sendBeacon(CONFIG.endpoint, new Blob([payload], { type: 'application/json' }));
      if (!ok) { pending = batch.concat(pending); persistQueue(); }
      return;
    }
    fetch(CONFIG.endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); })
      .catch(function () { pending = batch.concat(pending); persistQueue(); });
  }

  function startFlushLoop() {
    if (flushTimer) return;
    flushTimer = window.setInterval(flush, CONFIG.batchIntervalMs);
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') { markVisibility('hidden'); flush(true); sendChunk(true); }
      else markVisibility('visible');
    });
    // A restored Firefox document may skip visibilitychange on Back/Forward.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted && document.visibilityState !== 'hidden') markVisibility('visible');
    });
  }

  // Sichtbarkeit als Schnittmarke fuer den Player: Wiedergegeben wird nur, was
  // im gerade sichtbaren Tab passierte. Beim Zurueckkehren ersetzt ein frischer
  // Snapshot das inzwischen veraltete Bild, damit der Abschnitt fuer sich
  // abspielbar ist.
  function markVisibility(state) {
    if (!rrStarted || !window.rrwebRecord) return;
    try { window.rrwebRecord.addCustomEvent('sr_visibility', { state: state }); } catch (e) { }
    if (state === 'visible' && typeof window.rrwebRecord.takeFullSnapshot === 'function') {
      try { window.rrwebRecord.takeFullSnapshot(true); } catch (e) { }
    }
  }

  // --------------------------------------------------------------------------
  // Session-Recording (rrweb) — das "Video"
  // --------------------------------------------------------------------------
  var rrBuffer = [];
  var rrBufferBytes = 0;
  // Chunk-Nummern zaehlen je Seitenaufruf (viewId) ab 1. Parallele Tabs
  // derselben Session kommen sich so nicht in die Quere.
  var rrSeq = 0;
  var rrStarted = false;
  var rrStopFn = null;
  var rrSnapshotGraceTimer = null;
  var rrSnapshotGraceUntil = 0;
  var recordingTimer = null;
  var retryTimer = null;
  var privacyObserver = null;
  var retryDbPromise = null;
  var retryRunning = false;
  // Events already removed from rrBuffer still need an unload-safe owner
  // until IndexedDB commits them or the server acknowledges their batch ID.
  var pendingRecordingBatches = new Map();

  function openRetryDb() {
    if (!window.indexedDB) return Promise.resolve(null);
    if (retryDbPromise) return retryDbPromise;
    retryDbPromise = new Promise(function (resolve) {
      var request = indexedDB.open('session-replay-recordings', 1);
      request.onupgradeneeded = function () {
        var db = request.result;
        if (!db.objectStoreNames.contains('chunks')) db.createObjectStore('chunks', { keyPath: 'batchId' });
      };
      request.onsuccess = function () { resolve(request.result); };
      request.onerror = function () { resolve(null); };
    });
    return retryDbPromise;
  }

  function retryStore(mode, value) {
    return openRetryDb().then(function (db) {
      if (!db) return mode === 'list' ? [] : mode === 'count' ? 0 : false;
      return new Promise(function (resolve) {
        var fallback = mode === 'list' ? [] : mode === 'count' ? 0 : false;
        try {
        var readOnly = mode === 'list' || mode === 'count';
        var tx = db.transaction('chunks', readOnly ? 'readonly' : 'readwrite');
        var store = tx.objectStore('chunks');
        var request = mode === 'put' ? store.put(value)
          : mode === 'delete' ? store.delete(value)
          : mode === 'count' ? store.count()
          : store.getAll();
        // A successful put request is not a committed transaction. Navigation
        // or quota errors can still abort it after request.onsuccess.
        tx.oncomplete = function () {
          resolve(mode === 'list' ? request.result || [] : mode === 'count' ? request.result || 0 : true);
        };
        tx.onabort = tx.onerror = request.onerror = function () { resolve(fallback); };
        } catch (e) { resolve(fallback); }
      });
    });
  }

  function trimRetryStore() {
    return retryStore('list').then(function (items) {
      var cutoff = now() - CONFIG.recording.retryMaxAgeMs;
      items.sort(function (a, b) { return (a.createdAt || 0) - (b.createdAt || 0); });
      var remove = items.filter(function (item, index) {
        return (item.createdAt || 0) < cutoff || index < items.length - CONFIG.recording.maxRetryBatches;
      });
      return Promise.all(remove.map(function (item) { return retryStore('delete', item.batchId); }));
    });
  }

  var EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;
  var IBAN_RE = /\b[A-Z]{2}\d{2}[A-Z0-9]{11,30}\b/gi;
  // Heuristik: trifft absichtlich breit, weil Telefonnummern in vielen
  // Schreibweisen auftreten. Sie trifft dadurch aber auch jede laengere
  // Ziffernfolge — deshalb nur auf Freitext anwenden, nie auf Struktur.
  var PHONE_RE = /(?:\+?\d[\d\s()\/-]{7,}\d)/g;

  // Struktur-Attribute enthalten keine personenbezogenen Daten, wurden von der
  // Telefon-Heuristik aber zerstoert: im Bestand traf sie u. a. 824 "d"- und
  // 1019 "viewBox"-Werte (SVG unbrauchbar), 1157 "id"- und 99 "class"-Werte
  // (Section-CSS greift nicht mehr -> falsche Farben) und 170 Inline-Styles.
  // E-Mail und IBAN bleiben auch hier maskiert, das sind eindeutige Muster.
  var STRUCTURAL_ATTR = /^(?:d|viewBox|points|preserveAspectRatio|pathLength|transform|gradientTransform|patternTransform|x|y|x1|x2|y1|y2|dx|dy|fx|fy|cx|cy|r|rx|ry|width|height|opacity|offset|fill|fill-opacity|fill-rule|stroke|stroke-width|stroke-dasharray|stroke-dashoffset|stroke-linecap|stroke-linejoin|stroke-miterlimit|stroke-opacity|stop-color|stop-opacity|clip-path|clip-rule|mask|filter|vector-effect|shape-rendering|markerWidth|markerHeight|refX|refY|gradientUnits|patternUnits|maskUnits|primitiveUnits|spreadMethod|id|class|style|_cssText|rel|type|role|tabindex|colspan|rowspan|span|sizes|loading|decoding|fetchpriority|media|integrity|crossorigin|slot|part|is|dir|lang|for|form|headers|list|min|max|step|maxlength|minlength|aria-controls|aria-describedby|aria-labelledby|aria-owns|aria-flowto|aria-activedescendant|aria-hidden|aria-expanded|aria-selected|aria-current|aria-level|aria-posinset|aria-setsize)$|^data-[a-z-]*(?:id|ids|index|target|modal|slide|step|position|count)$/i;

  // Eine data:-URI ist Binaernutzlast, kein Freitext. Die PII-Heuristiken treffen
  // darin zwangslaeufig — allein das case-insensitive IBAN-Muster passt in einem
  // 120-KB-Bild rund 20-mal auf beliebige base64-Zeichen. Jeder Treffer schneidet
  // ein Stueck aus der Nutzlast, das Bild ist im Replay danach kaputt. Maskiert
  // wird deshalb nur der Text NEBEN den data:-URIs.
  var DATA_URI_RE = /data:[a-z0-9.+-]+\/[a-z0-9.+-]+(?:;[a-z0-9.+-]+=[^;,]*)*;base64,[A-Za-z0-9+\/=]+/gi;
  function redactOutsideDataUris(value, redact) {
    var text = String(value || '');
    if (text.indexOf('data:') === -1) return redact(text);
    var out = '', last = 0, match;
    DATA_URI_RE.lastIndex = 0;
    while ((match = DATA_URI_RE.exec(text)) !== null) {
      out += redact(text.slice(last, match.index)) + match[0];
      last = match.index + match[0].length;
    }
    return out + redact(text.slice(last));
  }

  function redactText(value) {
    // IBAN vor Telefon: die Telefon-Heuristik wuerde eine IBAN sonst zuerst
    // treffen und als "Telefon" beschriften.
    return redactOutsideDataUris(value, function (text) {
      return text.replace(EMAIL_RE, '[E-Mail maskiert]')
        .replace(IBAN_RE, '[IBAN maskiert]')
        .replace(PHONE_RE, '[Telefon maskiert]');
    });
  }
  function redactUnambiguous(value) {
    return redactOutsideDataUris(value, function (text) {
      return text.replace(EMAIL_RE, '[E-Mail maskiert]')
        .replace(IBAN_RE, '[IBAN maskiert]');
    });
  }
  function redactCssText(value) {
    // CSS-Selektoren enthalten Shopify-Section-IDs. Die breite Telefon-Heuristik
    // darf diese nicht in [Telefon maskiert] umwandeln; sonst greift das Layout
    // im Replay nicht mehr. Sichtbarer Text aus content: bleibt maskiert.
    return redactUnambiguous(value)
      .replace(/\/\*[\s\S]*?\*\//g, function (comment) { return redactText(comment); })
      .replace(/(\bcontent\s*:\s*)(["'])(.*?)\2/gi, function (_, prefix, quote, content) {
        return prefix + quote + redactText(content) + quote;
      });
  }

  function sanitizeRecordingValue(value, key, seen, inStyle) {
    if (typeof value === 'string') {
      // Reine Binaerfelder von rrweb: Canvas-Snapshots liegen als rohes base64
      // ohne data:-Praefix vor, die Schutzlogik in redactOutsideDataUris greift
      // dort also nicht. Sie enthalten nie Freitext und bleiben unveraendert.
      if (/^(?:base64|rr_dataURL|fontSource)$/i.test(key || '')) return value;
      if (inStyle && key === 'textContent') return redactCssText(value);
      // DOM-Attribute: Origin erhalten, sonst laedt im Replay kein Bild/Style.
      if (/^(?:src|href|poster|xlink:href|data-src)$/i.test(key || '')) return sanitizeAssetUrl(value);
      if (/^(?:srcset|data-srcset|imagesrcset)$/i.test(key || '')) return sanitizeAssetSrcset(value);
      // Eigene Telemetriefelder: nur der Pfad ist relevant — ausser beim
      // Referrer, dessen Aussage im fremden Host steckt.
      if (/^referrer$/i.test(key || '')) return sanitizeReferrerUrl(value);
      if (/^(?:url|baseUrl)$/i.test(key || '')) return stripUrlSecrets(value);
      if (key && STRUCTURAL_ATTR.test(key)) return redactUnambiguous(value);
      return redactText(value);
    }
    if (!value || typeof value !== 'object') return value;
    seen = seen || [];
    if (seen.indexOf(value) !== -1) return value;
    seen.push(value);
    if (Array.isArray(value)) return value.map(function (item) { return sanitizeRecordingValue(item, '', seen, inStyle); });
    var clean = {};
    var childInStyle = inStyle || (value.type === 2 && String(value.tagName).toLowerCase() === 'style');
    Object.keys(value).forEach(function (name) { clean[name] = sanitizeRecordingValue(value[name], name, seen, childInStyle); });
    return clean;
  }

  function maskRecordedInput(value, element) {
    // Only public product/cart quantities; all other numeric inputs stay masked.
    if (element && element.type === 'number' &&
        (element.name === 'quantity' || element.name === 'updates[]') &&
        /^\d{1,6}$/.test(String(value))) return String(value);
    return String(value || '').replace(/./g, '*');
  }

  function applyPrivacyClasses(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var selectors = [
      '[data-customer-addresses]', '[data-customer-details]', '[data-order-details]',
      '.customer.account', '.customer.addresses', '.order-details', '.customer-address',
      '[class*="customer-email"]', '[class*="customer-phone"]', '[class*="shipping-address"]',
      '[class*="billing-address"]'
    ];
    selectors.forEach(function (selector) {
      try {
        if (scope.matches && scope.matches(selector)) scope.classList.add('sr-mask');
        scope.querySelectorAll(selector).forEach(function (el) { el.classList.add('sr-mask'); });
      } catch (e) { }
    });
  }

  function splitEventsByBytes(events, maxBytes) {
    var limit = maxBytes || CONFIG.recording.maxChunkBytes;
    var chunks = [], current = [], bytes = 0;
    events.forEach(function (event) {
      var eventBytes = jsonBytes(event) + 1;
      // Neben der Byte-Grenze auch die Event-Obergrenze des Backends einhalten:
      // ein Chunk mit mehr als 1000 Events wird dort mit 400 abgelehnt.
      if (current.length && (bytes + eventBytes > limit
        || current.length >= CONFIG.recording.maxChunkEvents)) {
        chunks.push(current); current = []; bytes = 0;
      }
      current.push(event); bytes += eventBytes;
    });
    if (current.length) chunks.push(current);
    return chunks;
  }

  function createRecordingBatch(events, seq) {
    return {
      batchId: 'recording:' + uuid(), sessionId: sessionId, customerKey: customerKey,
      viewId: viewId, seq: seq, sentAt: now(), createdAt: now(), meta: metaPayload(), events: events
    };
  }

  // gzip-Status je Session merken: 'ok' = bestätigt, 'off' = Proxy-Kette kann es
  // nicht. So wird höchstens einmal pro Session vergeblich komprimiert gesendet.
  function gzipState() { try { return sessionStore.getItem(CONFIG.storagePrefix + 'gzip') || ''; } catch (e) { return ''; } }
  function setGzipState(value) { try { sessionStore.setItem(CONFIG.storagePrefix + 'gzip', value); } catch (e) { } }

  function gzipPayload(json) {
    if (!CONFIG.recording.gzipUploads || gzipState() === 'off') return Promise.resolve(null);
    if (typeof CompressionStream === 'undefined' || !window.Response || !window.Blob) return Promise.resolve(null);
    try {
      var stream = new Blob([json]).stream().pipeThrough(new CompressionStream('gzip'));
      return new Response(stream).arrayBuffer().catch(function () { return null; });
    } catch (e) { return Promise.resolve(null); }
  }

  function postRecordingPayload(json, keepAlive) {
    function plain() {
      return fetch(CONFIG.recording.endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: json, keepalive: keepAlive
      });
    }
    return gzipPayload(json).then(function (packed) {
      if (!packed) return plain();
      return fetch(CONFIG.recording.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Content-Encoding': 'gzip' },
        body: packed, keepalive: keepAlive
      }).then(function (res) {
        if (res.ok) { setGzipState('ok'); return res; }
        // Versteht die Proxy-Kette den komprimierten Body nicht, kommt kein 413
        // zurück, sondern ein Parser-Fehler. Dann dauerhaft unkomprimiert senden.
        var unsupported = res.status === 400 || res.status === 415 || res.status === 501;
        if (!unsupported || gzipState() === 'ok') return res;
        setGzipState('off');
        return plain();
      });
    });
  }

  function sendRecordingBatch(batch, useBeacon) {
    if (!hasConsent() || trackingExcluded || consentWasRevoked) return Promise.resolve(false);
    var payload = JSON.stringify(batch);
    if (useBeacon && navigator.sendBeacon && payload.length < 60000) {
      // Der Browser hat den Beacon uebernommen und stellt ihn selbst zu. Der
      // Batch bleibt trotzdem in IndexedDB, bis ein Retry ihn bestaetigt — der
      // Server verwirft ihn dann anhand derselben batchId als Duplikat.
      // 'queued' unterscheidet diesen Fall von einem echten Fehlschlag: bei
      // "false" wuerden die Events zurueck in den Puffer wandern und spaeter
      // unter einer NEUEN batchId ein zweites Mal ankommen — im Replay
      // erscheinen dieselben DOM-Mutationen dann doppelt.
      var queued = navigator.sendBeacon(CONFIG.recording.endpoint, new Blob([payload], { type: 'application/json' }));
      return Promise.resolve(queued ? 'queued' : false);
    }
    return postRecordingPayload(payload, payload.length < 60000).then(function (res) {
      if (res.ok) {
        pendingRecordingBatches.delete(batch.batchId);
        return retryStore('delete', batch.batchId).then(function () { return true; });
      }
      if (res.status === 413) {
        if (batch.events.length > 1) {
          var midpoint = Math.ceil(batch.events.length / 2);
          var parts = [batch.events.slice(0, midpoint), batch.events.slice(midpoint)].map(function (events) {
            return createRecordingBatch(events, batch.seq);
          });
          return retryStore('delete', batch.batchId).then(function () {
            return Promise.all(parts.map(function (part) {
              return retryStore('put', part).then(function () { return sendRecordingBatch(part, false); });
            }));
          });
        }
        // Ein einzelnes Event lässt sich nicht weiter teilen. Endlos-Retry würde
        // die Warteschlange blockieren und Datenvolumen des Besuchers verbrennen,
        // also verwerfen und die Lücke im Recording protokollieren.
        return retryStore('delete', batch.batchId).then(function () {
          reportDroppedChunk(batch);
          return true;
        });
      }
      if (res.status === 429) {
        var wait = Math.min(60000, Math.max(2000, Number(res.headers.get('Retry-After') || 2) * 1000));
        window.setTimeout(processRetryQueue, wait);
        return false;
      }
      throw new Error('HTTP ' + res.status);
    }).catch(function () { return false; });
  }

  // Verworfenen Chunk als Custom-Event melden, damit die Lücke im Player
  // sichtbar wird statt wie ein Hänger des Replays zu wirken.
  function reportDroppedChunk(batch) {
    if (!rrStarted || !window.rrwebRecord) return;
    try {
      window.rrwebRecord.addCustomEvent('chunk_dropped', {
        seq: batch.seq,
        events: batch.events.length,
        bytes: jsonBytes(batch.events),
        eventTypes: batch.events.slice(0, 10).map(function (e) { return e && e.type; })
      });
    } catch (e) { }
  }

  // Ein Upload, der beim Seitenwechsel abbricht, lag bisher bis zum NÄCHSTEN
  // Seitenaufruf in IndexedDB. Verließ der Besucher den Shop vorher, war der
  // Chunk endgültig verloren — inklusive Bild-Snapshots ganzer Seitenaufrufe.
  // Deshalb zusätzlich zyklisch und bei Rückkehr der Verbindung nachliefern.
  // Vorab per count() prüfen, damit der Timer nicht alle 30 s die komplette
  // Warteschlange (bis zu 80 Batches) aus IndexedDB liest.
  function processRetryQueueIfPending() {
    if (retryRunning || !hasConsent()) return;
    drainParkedBatches().then(function () { return retryStore('count'); })
      .then(function (pending) { if (pending > 0) processRetryQueue(); });
  }

  function processRetryQueue() {
    if (retryRunning || !hasConsent()) return;
    retryRunning = true;
    trimRetryStore().then(function () { return retryStore('list'); }).then(function (items) {
      items.sort(function (a, b) { return (a.createdAt || 0) - (b.createdAt || 0); });
      return items.reduce(function (promise, batch) {
        return promise.then(function () { return sendRecordingBatch(batch, false); });
      }, Promise.resolve());
    }).finally(function () { retryRunning = false; });
  }

  function addRecordingMarker(type, data) {
    // Nur ausgewählte Typen als Marker (Timeline im Player)
    if (!rrStarted || !window.rrwebRecord) return;
    if (type === 'click' || type === 'cart_action' || type === 'page_view' || type === 'product_view' || type === 'form_submit' || /^catalog_(open|page_view|page_dwell|zoom|close|download|link|fullscreen|error)$/.test(type)) {
      try { window.rrwebRecord.addCustomEvent('sr_' + type, data || {}); } catch (e) { }
    }
  }

  function takeRecordingEventsForUpload() {
    var rawEvents = rrBuffer.splice(0, rrBuffer.length);
    rrBufferBytes = 0;
    // rrweb ergänzt bei inlineImages die rr_dataURL asynchron am bereits
    // emittierten Snapshot-Objekt. Erst jetzt klonen/sanitizen, damit diese
    // nachgeladenen Bilddaten nicht durch eine zu frühe Kopie verloren gehen.
    return rawEvents.map(function (event) { return sanitizeRecordingValue(event, '', []); });
  }

  // Beim Seitenwechsel wird die Seite abgebaut, bevor Promises weiterlaufen:
  // IndexedDB-put, gzip-Kompression und der anschliessende Upload kamen dort
  // regelmaessig nie an — der letzte Chunk jeder Seite fehlte, im Player stand
  // das Bild bis zum naechsten Seitenaufruf still. Dieser Pfad arbeitet deshalb
  // ausschliesslich synchron: Beacons bis zum keepalive-Budget, alles andere
  // (und bei Platz auch die Beacon-Kopien) in localStorage parken. Doppelt
  // Ankommendes verwirft der Server anhand der batchId.
  var UNLOAD_PARK_KEY = 'rrUnloadParked';

  function parkBatches(batches) {
    if (!batches.length) return true;
    var parked = storage.get(UNLOAD_PARK_KEY, []);
    if (!Array.isArray(parked)) parked = [];
    try {
      var byId = new Map();
      parked.concat(batches).forEach(function (batch) { byId.set(batch.batchId, batch); });
      window.localStorage.setItem(CONFIG.storagePrefix + UNLOAD_PARK_KEY, JSON.stringify(Array.from(byId.values())));
      return true;
    } catch (e) { return false; }
  }

  function sendChunkOnUnload() {
    var batches = Array.from(pendingRecordingBatches.values());
    if (rrBuffer.length) {
      var events = takeRecordingEventsForUpload();
      rrSeq++;
      splitEventsByBytes(events, CONFIG.recording.unloadChunkBytes).forEach(function (part) {
        var batch = createRecordingBatch(part, rrSeq);
        pendingRecordingBatches.set(batch.batchId, batch);
        batches.push(batch);
      });
    }
    if (!batches.length) return;
    var budget = CONFIG.recording.unloadBeaconBytes;
    var beaconed = [], unsent = [];
    batches.forEach(function (batch) {
      var payload = JSON.stringify(batch);
      var bytes = jsonBytes(payload);
      var queued = false;
      if (navigator.sendBeacon && bytes <= budget) {
        try { queued = navigator.sendBeacon(CONFIG.recording.endpoint, new Blob([payload], { type: 'application/json' })); } catch (e) { }
      }
      if (queued) { budget -= bytes; beaconed.push(batch); } else unsent.push(batch);
    });
    // Erst alles parken; reicht der Platz nicht, zumindest die ungesendeten.
    // On quota failure the pending map retains the original IDs for resume.
    if (!parkBatches(unsent.concat(beaconed))) parkBatches(unsent);
  }

  // Geparkte Batches in die IndexedDB-Warteschlange uebernehmen. Ohne IndexedDB
  // direkt senden; nur Bestaetigtes verlaesst localStorage.
  function drainParkedBatches() {
    var parked = storage.get(UNLOAD_PARK_KEY, []);
    if (!Array.isArray(parked) || !parked.length) return Promise.resolve();
    return Promise.all(parked.map(function (batch) {
      return retryStore('put', batch).then(function (ok) {
        return ok ? true : sendRecordingBatch(batch, false).then(function (sent) { return sent === true; });
      }).then(function (done) { return done ? batch.batchId : null; });
    })).then(function (doneIds) {
      var done = new Set(doneIds.filter(Boolean));
      // Keep the synchronous backup until the replacement storage commits.
      // Preserve batches added by an unload while this drain was in progress.
      var current = storage.get(UNLOAD_PARK_KEY, []);
      var left = (Array.isArray(current) ? current : []).filter(function (batch) { return !done.has(batch.batchId); });
      if (left.length) storage.set(UNLOAD_PARK_KEY, left);
      else storage.remove(UNLOAD_PARK_KEY);
    });
  }

  function sendChunk(useBeacon) {
    if (useBeacon) { sendChunkOnUnload(); return; }
    if (!rrBuffer.length) return;
    var events = takeRecordingEventsForUpload();
    rrSeq++;
    splitEventsByBytes(events).forEach(function (part) {
      var batch = createRecordingBatch(part, rrSeq);
      pendingRecordingBatches.set(batch.batchId, batch);
      retryStore('put', batch).then(function (ok) {
        if (!hasConsent() || trackingExcluded || consentWasRevoked) {
          pendingRecordingBatches.delete(batch.batchId);
          return retryStore('delete', batch.batchId);
        }
        if (ok) pendingRecordingBatches.delete(batch.batchId);
        else parkBatches([batch]);
        return sendRecordingBatch(batch, useBeacon);
      });
    });
  }

  function serializedNodeHasPendingImage(node) {
    if (!node || typeof node !== 'object') return false;
    if (String(node.tagName || '').toLowerCase() === 'img') {
      var attrs = node.attributes || {};
      if (!attrs.rr_dataURL && !attrs.rr_dataurl) return true;
    }
    return Array.isArray(node.childNodes) && node.childNodes.some(serializedNodeHasPendingImage);
  }

  function eventNeedsImageGrace(event) {
    if (!event || !event.data) return false;
    if (event.type === 2) return serializedNodeHasPendingImage(event.data.node);
    if (event.type === 3 && event.data.source === 0 && Array.isArray(event.data.adds)) {
      return event.data.adds.some(function (addition) {
        return serializedNodeHasPendingImage(addition && addition.node);
      });
    }
    return false;
  }

  function bufferRecordingEvent(event) {
    rrBuffer.push(event);
    rrBufferBytes += jsonBytes(event);
    if (eventNeedsImageGrace(event)) {
      // Snapshots und neu eingefügte Bildknoten werden von rrweb sofort emittiert,
      // Bilddaten aber erst in dessen load-Handler nachgetragen. Während dieser
      // kurzen Frist nicht senden; sonst bleibt im Replay das kaputte src-Bild.
      rrSnapshotGraceUntil = Date.now() + 5000;
      if (rrSnapshotGraceTimer) window.clearTimeout(rrSnapshotGraceTimer);
      rrSnapshotGraceTimer = window.setTimeout(function () {
        rrSnapshotGraceTimer = null;
        rrSnapshotGraceUntil = 0;
        if (rrBuffer.length) sendChunk();
      }, 5000);
    }
    var overLimit = rrBuffer.length >= CONFIG.recording.maxBufferEvents || rrBufferBytes >= CONFIG.recording.maxChunkBytes;
    if (overLimit && Date.now() >= rrSnapshotGraceUntil) sendChunk();
  }

  function startRecording() {
    if (rrStarted || !CONFIG.recording.enabled) return;
    // Der Checkout-Web-Pixel kann damit seine Funnel-Ereignisse derselben
    // (bereits consentierten) Replay-Session zuordnen.
    try { document.cookie = '_sr_sid=' + encodeURIComponent(sessionId) + '; Path=/; Max-Age=1800; SameSite=Lax; Secure'; } catch (e) { }
    var s = document.createElement('script');
    // Zusammen mit dem CORS-Header der eigenen Bibliothek werden JS-Fehler
    // daraus mit Datei und Zeile statt nur als "Script error." sichtbar.
    s.crossOrigin = 'anonymous';
    s.src = CONFIG.recording.libUrl;
    s.async = true;
    s.onload = function () {
      // rrweb 2.x stellt ein Modulobjekt { record } bereit, 1.x direkt die
      // Funktion. Alle Aufrufer nutzen window.rrwebRecord(...) und
      // window.rrwebRecord.addCustomEvent — daher auf die Funktion normalisieren.
      if (window.rrwebRecord && typeof window.rrwebRecord !== 'function' && typeof window.rrwebRecord.record === 'function') {
        window.rrwebRecord = window.rrwebRecord.record;
      }
      if (trackingExcluded || !trackingActive || rrStarted || typeof window.rrwebRecord !== 'function' || !hasConsent()) return;
      rrStarted = true;
      applyPrivacyClasses(document);
      privacyObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) { mutation.addedNodes.forEach(function (node) { applyPrivacyClasses(node); }); });
      });
      privacyObserver.observe(document.documentElement, { childList: true, subtree: true });
      rrStopFn = window.rrwebRecord({
        emit: function (event) {
          bufferRecordingEvent(event);
        },
        // Welche Eingabetypen maskiert werden, steuert maskInputOptions (Suche
        // bleibt lesbar); maskInputFn ersetzt nur den Wert. Erkennbare PII
        // in Suchbegriffen entfernt sanitizeRecordingValue vor dem Upload.
        maskAllInputs: false,
        maskInputOptions: {
          color: true, date: true, 'datetime-local': true, email: true,
          month: true, number: true, range: true, tel: true, text: true,
          time: true, url: true, week: true, textarea: true, select: true,
          password: true, search: false
        },
        maskInputFn: maskRecordedInput,
        maskTextFn: function (text) { return redactText(text); },
        blockClass: 'sr-block',         // Elemente mit dieser Klasse werden nicht aufgezeichnet
        maskTextClass: 'sr-mask',       // Texte mit dieser Klasse werden maskiert
        collectFonts: true,
        inlineImages: true,             // auch Set-/Variantbilder stabil im Replay erhalten
        // Canvas-Aufzeichnung (z. B. Gravur-Vorschau). WebGL/3D wie <model-viewer>
        // wird von rrweb nicht zuverlässig erfasst — dort greift das Poster-Bild.
        recordCanvas: !!CONFIG.recording.recordCanvas,
        sampling: {
          mousemove: 60, scroll: 150, media: 800,
          canvas: CONFIG.recording.canvasFps || 4
        },
        dataURLOptions: { type: 'image/webp', quality: 0.6 }, // kleinere Canvas-Snapshots
        inlineStylesheet: true
      });
      // Im Hintergrund geoeffneter Tab: erst ab dem Wechsel dorthin relevant.
      if (document.visibilityState === 'hidden') markVisibility('hidden');
      recordingTimer = window.setInterval(sendChunk, CONFIG.recording.chunkIntervalMs);
      retryTimer = window.setInterval(processRetryQueueIfPending, CONFIG.recording.retryIntervalMs);
      window.addEventListener('pagehide', function () { sendChunk(true); });
      window.addEventListener('online', processRetryQueueIfPending);
      drainParkedBatches().then(processRetryQueue);
    };
    document.head.appendChild(s);
  }

  // JS-Fehler des Besuchers erfassen (Marker + Zähler für Session-Liste)
  // Bekannte harmlose Browser-Warnungen (kein echter Fehler) ausfiltern,
  // damit sie nicht faelschlich als Technik-Problem markiert werden.
  var IGNORED_ERROR_PATTERNS = [/^ResizeObserver loop/i];
  function isIgnoredError(message) {
    var msg = String(message || '');
    for (var i = 0; i < IGNORED_ERROR_PATTERNS.length; i++) {
      if (IGNORED_ERROR_PATTERNS[i].test(msg)) return true;
    }
    return false;
  }
  function initErrorCapture() {
    window.addEventListener('error', function (e) {
      if (!hasConsent() || !meta) return;
      if (isIgnoredError(e.message)) return;
      meta.errorCount++;
      try { sessionStore.setItem(CONFIG.storagePrefix + 'errorCount', String(meta.errorCount)); } catch (err) { }
      if (rrStarted && window.rrwebRecord) {
        try {
          window.rrwebRecord.addCustomEvent('js_error', {
            message: String(e.message || '').slice(0, 300),
            source: String(e.filename || '').slice(0, 200),
            line: e.lineno || null
          });
        } catch (err) { }
      }
    });
    // Unbehandelte Promise-Rejections (z. B. fetch ohne .catch)
    window.addEventListener('unhandledrejection', function (e) {
      if (!hasConsent() || !meta) return;
      meta.rejectionCount++;
      try { sessionStore.setItem(CONFIG.storagePrefix + 'rejectionCount', String(meta.rejectionCount)); } catch (err) { }
      if (rrStarted && window.rrwebRecord) {
        try {
          var reason = e.reason;
          window.rrwebRecord.addCustomEvent('js_rejection', {
            message: String((reason && reason.message) || reason || '').slice(0, 300)
          });
        } catch (err) { }
      }
    });
  }

  // Fehlgeschlagene Netzwerk-Requests (Reject oder Status >=400) erfassen.
  // Nur same-origin/Shop-Requests, um Drittanbieter-Rauschen zu vermeiden.
  function initNetworkErrorCapture() {
    if (!window.fetch) return;
    var origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var sameOrigin = !/^https?:\/\//i.test(url) || url.indexOf(window.location.origin) === 0;
      return origFetch.apply(this, arguments).then(function (res) {
        if (sameOrigin && !res.ok) recordNetworkError(url, res.status);
        return res;
      }, function (err) {
        if (sameOrigin) recordNetworkError(url, 0);
        throw err;
      });
    };
  }

  // Bekannter Theme-Bug (assets/global.js, unaufgeloester Liquid-Tag im Variant-Change-Handler):
  // fetch('/products/{{ product.handle }}?variant=...#variant-specifications') schlaegt IMMER mit 404 fehl.
  // Bis der Theme-Code gefixt ist, hier ausblenden, damit er Sessions nicht als "technisches Problem" markiert.
  var KNOWN_BROKEN_NETWORK_URL = /\{\{\s*product\.handle\s*\}\}/;

  function recordNetworkError(url, status) {
    if (!hasConsent() || !meta) return;
    if (KNOWN_BROKEN_NETWORK_URL.test(String(url))) return;
    meta.networkErrorCount++;
    try { sessionStore.setItem(CONFIG.storagePrefix + 'networkErrorCount', String(meta.networkErrorCount)); } catch (err) { }
    if (rrStarted && window.rrwebRecord) {
      try { window.rrwebRecord.addCustomEvent('network_error', { url: stripUrlSecrets(url).slice(0, 300), status: status }); } catch (err) { }
    }
  }

  // Ladezeit bewerten. Frueher wurde die komplette window.load-Dauer > 3 s als
  // Problem gemeldet. Die misst aber auch Apps, Pixel und Bilder weit unterhalb
  // des Viewports: ein voellig normaler Shopify-Shop liegt regelmaessig
  // darueber, das Signal war praktisch immer an. Stattdessen die beiden
  // Core-Web-Vitals-Werte, die der Besucher tatsaechlich spuert, jeweils an der
  // offiziellen "poor"-Schwelle:
  //   TTFB > 1800 ms — Server/Proxy/Theme antwortet langsam
  //   LCP  > 4000 ms — groesster sichtbarer Inhalt erscheint spaet
  var SLOW_TTFB_MS = 1800;
  var SLOW_LCP_MS = 4000;

  function reportSlowLoad(metric, durationMs) {
    if (!hasConsent() || !meta || meta.slowLoad) return;
    meta.slowLoad = true;
    try { sessionStore.setItem(CONFIG.storagePrefix + 'slowLoad', '1'); } catch (err) { }
    if (rrStarted && window.rrwebRecord) {
      try { window.rrwebRecord.addCustomEvent('slow_load', { metric: metric, durationMs: Math.round(durationMs) }); } catch (err) { }
    }
  }

  function initLoadTimeCapture() {
    if (!hasConsent() || !meta) return;
    if (meta.slowLoad) return; // schon in dieser Session erkannt
    try {
      var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
      var ttfb = nav ? nav.responseStart - nav.startTime : null;
      if (ttfb > SLOW_TTFB_MS) reportSlowLoad('ttfb', ttfb);
    } catch (err) { }

    if (typeof PerformanceObserver === 'undefined') return;
    try {
      var lcp = 0;
      var observer = new PerformanceObserver(function (list) {
        var entries = list.getEntries();
        if (entries.length) lcp = entries[entries.length - 1].startTime;
      });
      observer.observe({ type: 'largest-contentful-paint', buffered: true });
      // Der LCP-Wert steht erst fest, wenn der Besucher interagiert oder die
      // Seite verlaesst — bis dahin kann ein spaeteres Element ihn ueberholen.
      var finalized = false;
      function finalizeLcp() {
        if (finalized) return;
        finalized = true;
        try { observer.disconnect(); } catch (err) { }
        if (lcp > SLOW_LCP_MS) reportSlowLoad('lcp', lcp);
      }
      ['keydown', 'click', 'pagehide'].forEach(function (evt) {
        window.addEventListener(evt, finalizeLcp, { once: true, capture: true });
      });
      document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') finalizeLcp();
      });
    } catch (err) { }
  }

  // --------------------------------------------------------------------------
  // Analytics-Listener (v2: ohne mousemove/scroll/hover — steckt im Recording)
  // --------------------------------------------------------------------------
  function describeElement(el) {
    if (!el || !el.tagName) return null;
    var textElement = el;
    // Dawn appends this accessibility hint even to available variant labels.
    // Keep the variant name (including swatch help text), not the stock hint.
    if (el.tagName.toLowerCase() === 'label' && el.querySelector('.label-unavailable')) {
      textElement = el.cloneNode(true);
      textElement.querySelectorAll('.label-unavailable').forEach(function (hint) { hint.remove(); });
    }
    return {
      tag: el.tagName.toLowerCase(),
      id: el.id || null,
      classes: el.className && typeof el.className === 'string' ? el.className.split(/\s+/).slice(0, 5).join(' ') : null,
      text: redactText((textElement.textContent || '').trim().slice(0, 60)) || null,
      href: el.getAttribute ? stripUrlSecrets(el.getAttribute('href') || '') || null : null
    };
  }

  // Produktseiten-Interaktionen als eigene, auswertbare Signale erfassen.
  // Die Selektoren decken Dawn sowie gaengige Review-/Gallery-Apps ab; es
  // werden ausschliesslich Kategorie und Element-Metadaten gespeichert.
  function productInteractionCategory(el) {
    if (!window.__SR_PRODUCT || !el) return null;
    var node = el.closest ? (el.closest('a, button, summary, [role="button"], [role="tab"], img, video') || el) : el;
    var context = [node.id, node.className, node.getAttribute && node.getAttribute('aria-label'),
      node.getAttribute && node.getAttribute('title'), node.getAttribute && node.getAttribute('href'),
      node.textContent, node.parentElement && node.parentElement.className,
      node.parentElement && node.parentElement.textContent].filter(function (v) { return typeof v === 'string'; }).join(' ').toLowerCase();

    if (/(review|reviews|bewertung|bewertungen|judge\.me|judgeme|loox|yotpo|stamped)/.test(context)) return 'reviews';
    if (/(spezifikation|spezifikationen|specification|specifications|produktdetails|technische daten|technical details|variant-specifications)/.test(context)) return 'specifications';
    if (/(beschreibung|description|produktbeschreibung)/.test(context)) return 'description';
    if (/(groessentabelle|gr[oö]ssentabelle|size guide|size chart)/.test(context)) return 'size_guide';
    if (/(versand|lieferung|shipping|delivery|retoure|rueckgabe|rückgabe)/.test(context)) return 'shipping';
    if (/(faq|fragen|questions|accordion)/.test(context)) return 'faq';
    if (node.tagName === 'VIDEO' || /(^|[\s_-])(video|media-video)([\s_-]|$)/.test(context)) return 'video';
    if (node.tagName === 'IMG' || /(product-media|product__media|product-gallery|thumbnail|slider|zoom|lightbox)/.test(context)) return 'images';
    return null;
  }

  function initProductContentViews() {
    if (!window.__SR_PRODUCT || !window.IntersectionObserver) return;
    var selectors = {
      images: ['media-gallery', '[id*="MediaGallery"]', '.product__media-wrapper', '.product-gallery'],
      reviews: ['[id*="review" i]', '[class*="review" i]', '.jdgm-widget', '.loox-reviews'],
      specifications: ['#variant-specifications', '[id*="specification" i]', '[class*="specification" i]'],
      description: ['.product__description', '[id*="description" i]'],
      video: ['.product video', 'product-model', '[class*="product-video" i]']
    };
    var seen = {};
    var timers = new Map();
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var category = entry.target.getAttribute('data-sr-content-category');
        if (!category || seen[category]) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.35) {
          if (!timers.has(entry.target)) timers.set(entry.target, setTimeout(function () {
            if (!seen[category]) {
              seen[category] = true;
              track('content_view', { category: category });
            }
            observer.unobserve(entry.target);
            timers.delete(entry.target);
          }, 800));
        } else if (timers.has(entry.target)) {
          clearTimeout(timers.get(entry.target));
          timers.delete(entry.target);
        }
      });
    }, { threshold: [0.35] });
    Object.keys(selectors).forEach(function (category) {
      for (var i = 0; i < selectors[category].length; i++) {
        var nodes;
        try { nodes = document.querySelectorAll(selectors[category][i]); } catch (e) { nodes = []; }
        if (nodes.length) {
          Array.prototype.forEach.call(nodes, function (node) {
            node.setAttribute('data-sr-content-category', category);
            observer.observe(node);
          });
          break;
        }
      }
    });
  }

  // Bewertungs-Apps rendern Sterne, Rezensionen und
  // Fragen in ein eigenes <iframe>. Klicks darin erreichen die Listener des
  // Hauptdokuments nicht — die Sterne sahen dadurch aus wie "nicht getrackt".
  // Bei srcdoc-/same-origin-Frames laesst sich dasselbe Listener-Set direkt im
  // Frame-Dokument registrieren. Cross-Origin-Frames bleiben aussen vor.
  function iframeCategory(frame) {
    var node = frame;
    for (var i = 0; node && i < 4; i++) {
      var context = [node.id, typeof node.className === 'string' ? node.className : '',
        node.getAttribute && node.getAttribute('title'),
        node.getAttribute && node.getAttribute('name')].filter(Boolean).join(' ').toLowerCase();
      if (/(review|bewertung|rating|judgeme|jdgm|loox|yotpo|stamped)/.test(context)) return 'reviews';
      if (/(faq|frage|question)/.test(context)) return 'faq';
      node = node.parentElement;
    }
    return null;
  }

  function attachTrackedIframes() {
    var frames;
    try { frames = document.querySelectorAll('iframe:not([data-sr-tracked])'); } catch (e) { return; }
    Array.prototype.forEach.call(frames, function (frame) {
      var doc = null;
      try { doc = frame.contentDocument; } catch (e) { doc = null; } // cross-origin
      if (!doc) return;
      frame.setAttribute('data-sr-tracked', '1');
      var fallbackCategory = iframeCategory(frame);
      attachDocumentListeners(doc, fallbackCategory);
      // srcdoc-Frames werden von der App teils noch einmal neu geschrieben.
      frame.addEventListener('load', function () {
        try { if (frame.contentDocument && frame.contentDocument !== doc) attachDocumentListeners(frame.contentDocument, fallbackCategory); } catch (e) { }
      });
    });
  }

  function attachDocumentListeners(doc, fallbackCategory) {
    if (!doc || doc.__srListeners) return;
    doc.__srListeners = true;

    doc.addEventListener('click', function (e) {
      var target = e.target.closest ? (e.target.closest('a, button, [role="button"], input[type="submit"]') || e.target) : e.target;
      track('click', { element: describeElement(target), x: e.pageX, y: e.pageY });
      var interaction = productInteractionCategory(e.target) || (window.__SR_PRODUCT ? fallbackCategory : null);
      if (interaction) track('content_interaction', { category: interaction, element: describeElement(target) });
    }, true);

    // Formulare: NUR Metadaten, NIEMALS Werte
    doc.addEventListener('focusin', function (e) {
      var el = e.target;
      if (!el || !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName)) return;
      if (el.type === 'password') return;
      track('form_focus', {
        field: { name: el.name || null, type: el.type || el.tagName.toLowerCase(), id: el.id || null },
        formId: el.form ? (el.form.id || el.form.getAttribute('action') || null) : null
      });
    }, true);

    doc.addEventListener('submit', function (e) {
      var form = e.target;
      var action = form.getAttribute ? (form.getAttribute('action') || '') : '';
      track('form_submit', { formId: form.id || null, action: action || null });
      if (action.indexOf('/cart/add') !== -1) { markCart(); track('cart_action', { action: 'add', via: 'form', url: action }); }
    }, true);
  }

  function initListeners() {
    attachDocumentListeners(document, null);

    initProductContentViews();

    // Widget-Iframes werden erst nach dem App-Bundle eingehaengt. Der Observer
    // laeuft auf jeder DOM-Mutation, deshalb nur gebuendelt nachsehen.
    attachTrackedIframes();
    var iframeScan = null;
    new MutationObserver(function () {
      if (iframeScan) return;
      iframeScan = window.setTimeout(function () { iframeScan = null; attachTrackedIframes(); }, 500);
    }).observe(document.documentElement, { childList: true, subtree: true });

    // Shopify AJAX Cart API abfangen
    var origFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var result = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        markCart();
        result.then(function (res) {
          track('cart_action', { action: (url.match(/\/cart\/(\w+)/) || [])[1] || 'unknown', status: res.status, url: url.split('?')[0] });
        }).catch(function () { track('cart_action', { action: 'failed', url: url.split('?')[0] }); });
      }
      return result;
    };
  }

  // --------------------------------------------------------------------------
  // Stilles Widget (unverändert, verborgen)
  // --------------------------------------------------------------------------
  var widgetSort = 'recent';
  function renderWidget() {
    var root = document.getElementById('sr-widget');
    if (!root) return;
    var products = loadProducts();
    if (!products.length) { root.innerHTML = ''; root.hidden = true; return; }
    root.hidden = true;
    var sorted = products.slice().sort(function (a, b) {
      return widgetSort === 'frequency'
        ? (b.viewCount - a.viewCount) || (b.lastViewedAt - a.lastViewedAt)
        : b.lastViewedAt - a.lastViewedAt;
    });
    var html = '<div class="sr-widget__inner"><div class="sr-widget__header">' +
      '<h3 class="sr-widget__title">Zuletzt angesehen</h3>' +
      '<button type="button" class="sr-widget__toggle" data-sr-toggle>' +
      (widgetSort === 'recent' ? 'Sortierung: Neueste' : 'Sortierung: Häufigkeit') +
      '</button></div><ul class="sr-widget__list">';
    sorted.forEach(function (p) {
      html += '<li class="sr-widget__item"><a class="sr-widget__link" href="' + encodeURI(p.url || '#') + '">' +
        (p.image ? '<img class="sr-widget__img" src="' + encodeURI(p.image) + '" alt="" loading="lazy">' : '') +
        '<span class="sr-widget__name"></span><span class="sr-widget__meta"></span></a></li>';
    });
    root.innerHTML = html + '</ul></div>';
    var items = root.querySelectorAll('.sr-widget__item');
    sorted.forEach(function (p, i) {
      var item = items[i];
      if (!item) return;
      item.querySelector('.sr-widget__name').textContent = p.title;
      item.querySelector('.sr-widget__meta').textContent = (p.price != null ? p.price + ' · ' : '') + p.viewCount + '× angesehen';
    });
    var toggle = root.querySelector('[data-sr-toggle]');
    if (toggle) toggle.addEventListener('click', function () {
      widgetSort = widgetSort === 'recent' ? 'frequency' : 'recent';
      renderWidget();
    });
  }

  // --------------------------------------------------------------------------
  // Öffentliche API
  // --------------------------------------------------------------------------
  window.SessionReplay = {
    track: track, flush: flush,
    canTrack: function () { return !!(trackingActive && !trackingExcluded && customerKey && sessionId && hasConsent()); },
    getTrackingState: function () { return trackingExcluded ? 'excluded' : (!hasConsent() ? 'consent-required' : (trackingActive && customerKey && sessionId ? 'active' : 'inactive')); },
    getProducts: loadProducts,
    getQueue: function () { return pending.slice(); },
    getCustomerKey: function () { return customerKey; },
    getSessionId: function () { return sessionId; },
    isRecording: function () { return rrStarted; },
    startRecording: startRecording,
    version: RECORDER_VERSION,
    config: CONFIG,
    _test: {
      sendChunk: sendChunk, retryStore: retryStore,
      describeElement: describeElement,
      stripUrlSecrets: stripUrlSecrets, redactText: redactText,
      sanitizeReferrerUrl: sanitizeReferrerUrl, sessionReferrer: sessionReferrer,
      sanitizeRecordingValue: sanitizeRecordingValue,
      maskRecordedInput: maskRecordedInput,
      splitEventsByBytes: splitEventsByBytes, sendChunkOnUnload: sendChunkOnUnload,
      drainParkedBatches: drainParkedBatches, stopForConsentRevocation: stopForConsentRevocation,
      bufferRecordingEvent: bufferRecordingEvent, takeRecordingEventsForUpload: takeRecordingEventsForUpload
    }
  };

  // --------------------------------------------------------------------------
  // Init — Recording startet nur bei Consent; reagiert auch auf spätere Zustimmung
  // --------------------------------------------------------------------------
  var trackingActive = false;
  var trackingExcluded = false;
  var consentWasRevoked = false;

  function stopForConsentRevocation(excluded) {
    if (!trackingActive) return;
    trackingActive = false;
    consentWasRevoked = !excluded;
    var oldSessionId = sessionId;
    var oldCustomerKey = customerKey;
    if (typeof rrStopFn === 'function') { try { rrStopFn(); } catch (e) { } }
    rrStopFn = null;
    rrStarted = false;
    if (recordingTimer) { window.clearInterval(recordingTimer); recordingTimer = null; }
    if (retryTimer) { window.clearInterval(retryTimer); retryTimer = null; }
    if (rrSnapshotGraceTimer) { window.clearTimeout(rrSnapshotGraceTimer); rrSnapshotGraceTimer = null; }
    rrSnapshotGraceUntil = 0;
    if (flushTimer) { window.clearInterval(flushTimer); flushTimer = null; }
    if (privacyObserver) { privacyObserver.disconnect(); privacyObserver = null; }
    pending = [];
    rrBuffer = [];
    rrBufferBytes = 0;
    pendingRecordingBatches.clear();
    if (queueKey) storage.remove(queueKey);
    if (productsKey) storage.remove(productsKey);
    storage.remove(UNLOAD_PARK_KEY);
    if (!excluded) storage.remove('guestId'); // Keep the identifier that enforces exclusion.
    SESSION_KEYS.forEach(function (key) { sessionStore.removeItem(CONFIG.storagePrefix + key); });
    SESSION_KEYS.concat(LEGACY_SESSION_KEYS).forEach(function (key) {
      try { window.sessionStorage.removeItem(CONFIG.storagePrefix + key); } catch (e) { }
    });
    // Keep an excluded session link so checkout pixels are excluded too.
    try { document.cookie = excluded
      ? '_sr_sid=' + encodeURIComponent(oldSessionId) + '; Path=/; Max-Age=1800; SameSite=Lax; Secure'
      : '_sr_sid=; Path=/; Max-Age=0; SameSite=Lax; Secure'; } catch (e) { }
    retryStore('list').then(function (items) {
      return Promise.all(items.filter(function (item) { return item.sessionId === oldSessionId; })
        .map(function (item) { return retryStore('delete', item.batchId); }));
    });
    if (!excluded && oldSessionId && oldCustomerKey) {
      var deletion = JSON.stringify({ sessionId: oldSessionId, customerKey: oldCustomerKey });
      try {
        fetch('/apps/session-replay/delete-session', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: deletion, keepalive: true
        }).catch(function () {});
      } catch (e) { }
    }
    customerKey = null; sessionId = null; productsKey = null; queueKey = null; meta = null;
  }

  function boot() {
    if (trackingExcluded || trackingActive || !hasConsent()) return;
    if (consentWasRevoked) { window.location.reload(); return; }
    trackingActive = true;
    prepareIdentity();
    prepareMeta();
    startActivityListeners();
    // /geo checks IP and persistent visitor exclusions before recording starts.
    // Bei track:false wird NICHTS getrackt/aufgezeichnet. Fail-open: bei
    // Fehler/Timeout wird normal getrackt, damit Serverprobleme nicht alles blockieren.
    var started = false;
    function startAll() {
      if (started || !trackingActive || !hasConsent()) return;
      started = true;
      if (window.__SR_PRODUCT && window.__SR_PRODUCT.id) {
        recordProductView(window.__SR_PRODUCT);
        track('product_view', { product: window.__SR_PRODUCT });
      } else {
        track('page_view', {});
      }
      initListeners();
      initErrorCapture();
      initNetworkErrorCapture();
      initLoadTimeCapture();
      startFlushLoop();
      renderWidget();
      startRecording();
      refreshCartToken();
    }

    if (!CONFIG.recording.geoUrl) { startAll(); return; }
    var done = false;
    try {
      fetch(CONFIG.recording.geoUrl + '?sid=' + encodeURIComponent(sessionId) + '&customerKey=' + encodeURIComponent(customerKey), { method: 'GET', mode: 'cors', credentials: 'omit' })
        .then(function (r) { return r.json(); })
        .then(function (d) { done = true; if (d && d.track === false) { trackingExcluded = true; stopForConsentRevocation(true); return; } startAll(); })
        .catch(function () { done = true; startAll(); });
    } catch (e) { startAll(); return; }
    window.setTimeout(function () { if (!done) startAll(); }, 1500);
  }

  function init() {
    // Zuerst die Consent-API laden, dann Consent auswerten
    ensurePrivacyApi(function () {
      function reconcileConsent() {
        if (hasConsent()) boot();
        else stopForConsentRevocation();
      }
      reconcileConsent();
      document.addEventListener('visitorConsentCollected', reconcileConsent);
      // Manche Banner lösen kein DOM-Event aus. Der leichte Polling-Fallback
      // bleibt aktiv, damit auch ein späterer Widerruf zuverlässig greift.
      window.setInterval(reconcileConsent, 2000);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
